import {StyleSheet} from "aphrodite";
import {useContext} from "react";

import {View, type StyleType} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {semanticColor, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {AppShellContext} from "@khan/shared-core/context";

import {SoundWaves} from "../sound-waves.tsx";

import headphoneIcon from "@phosphor-icons/core/fill/headphones-fill.svg";

const ON_INDICATOR_SIZE = 4;
const SOUND_WAVE_HEIGHT = 12;

interface Props {
    /**
     * Indicates whether the feature is on or off.
     */
    on: boolean;

    /**
     * The text that appears when the user hovers over the headphone button.
     */
    tooltip: string;

    /**
     * Indicates whether there is audio output.
     */
    speaking: boolean;

    /**
     * If true, the headphone button is disabled and cannot be clicked.
     */
    disabled?: boolean;

    /**
     * Optional style overrides that is applied to the button container.
     */
    style?: StyleType;

    /**
     * Callback that is triggered when the button is clicked.
     */
    onClick: () => void;
}

/**
 * A headphone button with a tooltip.
 *
 * This is used in the chat input component to enable text-to-speech.
 */
export const HeadphoneButton = (props: Props) => {
    const {
        disabled = false,
        on = false,
        speaking,
        style,
        tooltip,
        onClick,
    } = props;
    const {entrypoint} = useContext(AppShellContext);
    const iconSize = entrypoint === "classroom" ? "medium" : "small";

    return (
        <View style={[styles.container, style]}>
            <Tooltip content={tooltip}>
                <IconButton
                    onClick={onClick}
                    disabled={disabled}
                    aria-label={tooltip}
                    icon={headphoneIcon}
                    role="switch"
                    aria-checked={on}
                    kind="tertiary"
                    size={iconSize}
                />
            </Tooltip>
            {on && (
                <SoundWaves
                    disabled={disabled}
                    style={[
                        styles.statusIndicator,
                        !speaking && styles.onIndicator,
                        !speaking && disabled && styles.disabled,
                    ]}
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: "0 0 auto",
        maxHeight: spacing.xLarge_32,
        alignItems: "center",
        justifyContent: "center",
    },
    disabled: {
        backgroundColor: semanticColor.core.background.disabled.strong,
    },
    onIndicator: {
        position: "absolute",
        bottom: `calc(-${spacing.xxSmall_6}px - ${ON_INDICATOR_SIZE / 2}px)`,
        height: ON_INDICATOR_SIZE,
        width: ON_INDICATOR_SIZE,
        borderRadius: "50%",
        backgroundColor: semanticColor.core.background.instructive.default,
    },
    statusIndicator: {
        transition: "all 300ms ease",
        position: "absolute",
        bottom: `calc(-${spacing.xxSmall_6}px - ${SOUND_WAVE_HEIGHT / 2}px)`,
        height: SOUND_WAVE_HEIGHT,
    },
});
